import { ConfigStateService, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { FeatureConstants } from 'src/core/constants/feature-constant';
import { ObjectHelper } from 'src/core/helpers/object.helper';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { LLMConfigurationDto } from 'src/core/models/llm/llm-configuration.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { ConfigurationSettingsService } from 'src/core/services/configuration-settings/configuration-settings.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { FeatureService } from 'src/core/services/feature/feature.service';
import { PhraseTypeSettingsService } from 'src/core/services/settings/phrase-type-settings.service';

@Component({
  selector: 'ca-translation-settings',
  templateUrl: './translation-settings.component.html',
  styleUrls: ['./translation-settings.component.scss'],
  standalone: false,
})
export class TranslationSettingsComponent implements OnInit {
  translationSettingsForm: FormGroup;
  translationSettingDtos: ConfigurationSettingsDto[] = [];
  llmConfigurations: LLMConfigurationDto[] = [];
  translationTypeSelected: boolean;
  languagesDataSource: CallLanguageCodeDto[];
  originalLanguagesDataSource: CallLanguageCodeDto[];
  translatedLanguageCode: string;
  defaultTranslatedLanguageCode: string;
  defaultGenAITypeCode: string;
  defaultModel: string = '';
  originalLanguages: string[];
  translatedLanguages: CallLanguageCodeDto[] = [
    {
      id: 0,
      name: 'dummy',
      code: 'dummy',
      isVisible: false,
      disabled: false,
      isDeleted: false,
    },
  ];
  systemLanguagesDataSource: CallLanguageCodeDto[];
  whiteLabelingEnabled: boolean = false;

  private readonly settingKeyTranslationEnabled = 'Translation.Enabled';
  private readonly settingKeyTranslatedLanguageCode = 'Translation.TranslatedLanguageCode';
  private readonly settingKeyOriginalLanguageCode = 'Translation.OriginalLanguageCode';
  private readonly settingKeyLLMConfigurationId = 'Translation.LLMConfigurationId';

  get hasSummarizationFeature(): boolean {
    return this.featureService.isEnabled(FeatureConstants.ConversationSummarization);
  }

  get customizeSummarizationServiceInfo(): SafeHtml | string {
    return this.localizationService.instant('Settings::CustomizeSummarizationServiceInfo');
  }

  constructor(
    private config: ConfigStateService,
    private fb: FormBuilder,
    private settingService: ConfigurationSettingsService,
    private toastr: ToasterService,
    private crudService: CrudService,
    private localizationService: LocalizationService,
    private featureService: FeatureService,
    private phraseService: PhraseTypeSettingsService,
    private operators: Operators,
  ) {
    this.crudService
      .get<LLMConfigurationDto>(LLMConfigurationDto, {
        filters: [],
        sorters: [],
        skipCount: 0,
        maxResultCount: 9999,
      })
      .subscribe(response => {
        this.llmConfigurations = response.items;
      });

    this.whiteLabelingEnabled =
      this.config.getSetting('WhiteLabeling:Enabled')?.toLowerCase() === 'true';
    this.defaultTranslatedLanguageCode = this.config.getSetting('Global.SystemLanguageCode');
    let translatedLanguageCode = this.config.getSetting(this.settingKeyTranslatedLanguageCode);
    if (!translatedLanguageCode) {
      this.translatedLanguageCode = this.defaultTranslatedLanguageCode;
    } else {
      this.translatedLanguageCode = translatedLanguageCode;
    }
    let request = {
      filters: [
        {
          field: 'isVisible',
          operator: this.operators.Contains,
          value: true,
        },
        {
          field: 'withoutMixed',
          operator: this.operators.Contains,
          value: true,
        },
      ],
      sorters: [],
      maxResultCount: 99999,
      skipCount: 0,
    };
    this.settingService
      .getLanguageCodeList(request)
      .pipe(take(1))
      .subscribe(res => {
        this.systemLanguagesDataSource = res.items;
      });

    let translationEnabled =
      this.config.getSetting('Translation.Enabled')?.toLowerCase() === 'true' ? true : false;

    let llmConfigurationId;

    if (this.config.getSetting(this.settingKeyLLMConfigurationId)) {
      llmConfigurationId = Number(this.config.getSetting(this.settingKeyLLMConfigurationId));
    }

    this.originalLanguages = this.config
      .getSetting(this.settingKeyOriginalLanguageCode)
      .split(';')
      .filter(x => x.length > 0);

    this.translationSettingsForm = fb.group({
      translationEnabled: [translationEnabled],
      originalLanguages: [],
      translatedLanguageCode: this.translatedLanguageCode,
      llmConfigurationId: [llmConfigurationId],
    });

    this.phraseService.getLanguages(true).subscribe(res => {
      this.languagesDataSource = res;
      this.languagesDataSource.sort((x, y) => (x.name > y.name ? 1 : -1));
      this.resetOriginalLanguages();
      this.resetTranslatedLanguages();

      if (this.originalLanguagesDataSource.length > 0) {
        this.translationSettingsForm
          .get('originalLanguages')
          .setValue(this.originalLanguagesDataSource);
      }
      if (this.originalLanguages.length > 0) {
        this.translationSettingsForm.get('originalLanguages').setValue(this.originalLanguages);
      }
    });
  }

  ngOnInit(): void {}

  onSubmitTranslationSettings() {
    if (this.translationSettingsForm.invalid) {
      return;
    }

    this.translationSettingDtos = new Array();
    let translationEnabled = this.translationSettingsForm.get('translationEnabled').value;

    this.translationSettingDtos.push({
      settingName: this.settingKeyTranslationEnabled,
      settingValue: translationEnabled.toString(),
    });

    this.translationSettingDtos.push({
      settingName: this.settingKeyLLMConfigurationId,
      settingValue: translationEnabled
        ? this.translationSettingsForm.get('llmConfigurationId').value?.toString()
        : '',
    });

    this.translationSettingDtos.push({
      settingName: this.settingKeyOriginalLanguageCode,
      settingValue: translationEnabled
        ? this.translationSettingsForm.get('originalLanguages').value?.join(';')
        : '',
    });

    this.translationSettingDtos.push({
      settingName: this.settingKeyTranslatedLanguageCode,
      settingValue: translationEnabled
        ? this.translationSettingsForm.get('translatedLanguageCode').value.toString()
        : this.defaultTranslatedLanguageCode,
    });

    this.settingService.saveSetting(this.translationSettingDtos).subscribe(res => {
      this.toastr.success(
        this.localizationService.instant('AbpSettingManagement::SavedSuccessfully'),
      );
    });
  }

  removeFromSelectedOriginalLanguages(languageCode: string) {
    let selectedItems = this.translationSettingsForm.get('originalLanguages').value;

    if (selectedItems) {
      const indexToRemoveFromSelected = selectedItems.findIndex(i => i === languageCode);
      if (indexToRemoveFromSelected > -1) {
        selectedItems.splice(indexToRemoveFromSelected, 1);
        this.translationSettingsForm.get('originalLanguages').setValue(selectedItems);
      }
    }
  }

  onTranslationEnabledChanged(eventArgs) {
    if (eventArgs.target.checked) {
    } else {
    }
  }

  resetOriginalLanguages() {
    this.originalLanguagesDataSource = ObjectHelper.deepCopy(this.languagesDataSource);
  }

  resetTranslatedLanguages() {
    this.translatedLanguages = ObjectHelper.deepCopy(this.languagesDataSource);

    const alreadyExists = this.translatedLanguages.some(
      lang => lang.code === this.translatedLanguageCode,
    );
    if (!alreadyExists) {
      const index = this.systemLanguagesDataSource.findIndex(
        l => l.code == this.translatedLanguageCode,
      );
      if (index > -1) {
        this.translatedLanguages.push(this.systemLanguagesDataSource[index]);
      }
    }
    this.translatedLanguages.sort((x, y) => (x.name > y.name ? 1 : -1));
  }
}
